import styled from "@emotion/styled";
import type { CSSProperties } from "react";

import { Button, SkiliticsIcon, Tooltip, classNames } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatStrings } from "@skilitics/localization";

import { alignments, type Alignment } from "../types";

const GRID_BUTTON_SIZE = 24;
const GRID_GAP_SIZE = 4;

const HighlightSelection = styled.span`
  position: absolute;
  background: transparent;
  z-index: 1;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  opacity: 0.3;
  &.active {
    background: ${({ theme }) => theme.primary};
  }
  &.left {
    right: unset;
    width: ${GRID_GAP_SIZE}px;
  }
  &.top {
    bottom: unset;
    height: ${GRID_GAP_SIZE}px;
  }
  &.right {
    left: unset;
    width: ${GRID_GAP_SIZE}px;
  }
  &.bottom {
    top: unset;
    height: ${GRID_GAP_SIZE}px;
  }
  &.topRow {
    top: ${GRID_BUTTON_SIZE + GRID_GAP_SIZE + 1}px;
    bottom: unset;
    height: ${GRID_GAP_SIZE}px;
  }
  &.bottomRow {
    bottom: ${GRID_BUTTON_SIZE + GRID_GAP_SIZE + 1}px;
    top: unset;
    height: ${GRID_GAP_SIZE}px;
  }
  &.leftColumn {
    left: ${GRID_BUTTON_SIZE + GRID_GAP_SIZE + 1}px;
    right: unset;
    width: ${GRID_GAP_SIZE}px;
  }
  &.rightColumn {
    right: ${GRID_BUTTON_SIZE + GRID_GAP_SIZE + 1}px;
    left: unset;
    width: ${GRID_GAP_SIZE}px;
  }
`;
const BackgroundContainer = styled.div`
  position: relative;
  background: ${({ theme }) => theme.surfaceVariant};
  padding: ${GRID_GAP_SIZE}px;
`;

const EdgeContainer = styled.div`
  position: relative;
  padding: 2px;
`;

const GridCorner = styled.span`
  position: absolute;
  height: 4px;
  width: 4px;
  border: solid 1px ${({ theme }) => theme.primary};
  background: ${({ theme }) => theme.surface};
  z-index: 2;
  &.topLeft {
    top: 0;
    left: 0;
  }
  &.topRight {
    top: 0;
    right: 0;
  }
  &.bottomLeft {
    bottom: 0;
    left: 0;
  }
  &.bottomRight {
    bottom: 0;
    right: 0;
  }
`;

const GridEdge = styled.span`
  position: absolute;
  background: ${({ theme }) => theme.primary};
  z-index: 1;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  &.left {
    right: unset;
    width: 1px;
  }
  &.top {
    bottom: unset;
    height: 1px;
  }
  &.right {
    left: unset;
    width: 1px;
  }
  &.bottom {
    top: unset;
    height: 1px;
  }
`;

const ToggleButton = styled(Button)`
  height: 100%;
  width: 100%;
  min-width: 0;
  background: ${({ theme }) => theme.surface};
  border-radius: 0;
  font-size: 18px;
  i {
    display: none;
  }

  .inactive-icon {
    display: block;
  }

  &:hover {
    i {
      display: block;
      opacity: 0.5;
    }
    .inactive-icon {
      display: none;
    }
  }
  &.active {
    i {
      display: block;
    }
    .inactive-icon {
      display: none;
    }
  }
`;

const AlignmentGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, ${GRID_BUTTON_SIZE}px);
  grid-template-rows: repeat(3, ${GRID_BUTTON_SIZE}px);
  gap: ${GRID_GAP_SIZE}px;
  width: ${GRID_BUTTON_SIZE * 3 + GRID_GAP_SIZE * 2}px;
  height: ${GRID_BUTTON_SIZE * 3 + GRID_GAP_SIZE * 2}px;
  border: solid 1px ${({ theme }) => theme.outlineSoft};
  box-sizing: content-box;
`;

const GridItem = styled.div``;

const Dot = styled.span`
  height: 3px;
  width: 3px;
  border-radius: 5px;
  background: ${({ theme }) => theme.outlineSoft};
`;

type Props = {
  activeAlignment?: Alignment;
  onAlignmentChange: (alignment: Alignment) => void;
  flexDirection?: CSSProperties["flexDirection"];
  focussedInput?: keyof Pick<
    CSSProperties,
    "paddingLeft" | "paddingRight" | "paddingTop" | "paddingBottom" | "rowGap" | "columnGap"
  >;
  disabled?: boolean;
};

export const AlignmentGrid = ({
  activeAlignment = "alignTopLeft",
  onAlignmentChange,
  focussedInput,
  flexDirection,
  disabled,
}: Props) => {
  const formatAlignmentMessage = useFormatMessage(alignmentFormatStrings);
  return (
    <EdgeContainer>
      <GridCorner className="topLeft" />
      <GridCorner className="topRight" />
      <GridCorner className="bottomLeft" />
      <GridCorner className="bottomRight" />
      <GridEdge className="left" />
      <GridEdge className="top" />
      <GridEdge className="right" />
      <GridEdge className="bottom" />
      <BackgroundContainer>
        <HighlightSelection
          className={classNames({
            left: true,
            active: focussedInput === "paddingLeft",
          })}
        />
        <HighlightSelection
          className={classNames({
            top: true,
            active: focussedInput === "paddingTop",
          })}
        />
        <HighlightSelection
          className={classNames({
            right: true,
            active: focussedInput === "paddingRight",
          })}
        />
        <HighlightSelection
          className={classNames({
            bottom: true,
            active: focussedInput === "paddingBottom",
          })}
        />
        <HighlightSelection
          className={classNames({
            topRow: true,
            active: focussedInput === "rowGap",
          })}
        />
        <HighlightSelection
          className={classNames({
            bottomRow: true,
            active: focussedInput === "rowGap",
          })}
        />
        <HighlightSelection
          className={classNames({
            leftColumn: true,
            active: focussedInput === "columnGap",
          })}
        />
        <HighlightSelection
          className={classNames({
            rightColumn: true,
            active: focussedInput === "columnGap",
          })}
        />
        <AlignmentGridContainer>
          {alignments.map((alignment) => {
            const isActive = activeAlignment === alignment;
            return (
              <GridItem key={alignment}>
                <Tooltip
                  title={!disabled ? formatAlignmentMessage(alignment) : undefined}
                  placement={alignment.includes("Bottom") ? "bottom" : "top"}
                  disableFocusListener
                >
                  <span>
                    <ToggleButton
                      value={alignment}
                      size="small"
                      onClick={() => onAlignmentChange(alignment)}
                      className={isActive ? "active" : ""}
                      disabled={disabled}
                    >
                      {getIconFromAlignmentAndFlow(alignment, flexDirection)}
                      <Dot className="inactive-icon" />
                    </ToggleButton>
                  </span>
                </Tooltip>
              </GridItem>
            );
          })}
        </AlignmentGridContainer>
      </BackgroundContainer>
    </EdgeContainer>
  );
};

const alignmentFormatStrings: Record<Alignment, FormatStrings> = {
  alignTopLeft: {
    en: "Align Top Left",
  },
  alignTopCenter: {
    en: "Align Top Center",
  },
  alignTopRight: {
    en: "Align Top Right",
  },
  alignLeft: {
    en: "Align Left",
  },
  alignCenter: {
    en: "Align Center",
  },
  alignRight: {
    en: "Align Right",
  },
  alignBottomLeft: {
    en: "Align Bottom Left",
  },
  alignBottomCenter: {
    en: "Align Bottom Center",
  },
  alignBottomRight: {
    en: "Align Bottom Right",
  },
};

const getIconFromAlignmentAndFlow = (alignment: Alignment, flexDirection: Props["flexDirection"]) => {
  switch (alignment) {
    case "alignTopLeft":
      return (
        <SkiliticsIcon
          icon={"flow-horizontal-top-left"}
          flip={flexDirection === "column" ? "vertical" : flexDirection === "row-reverse" ? "horizontal" : undefined}
          rotation={flexDirection?.includes("column") ? 270 : undefined}
        />
      );
    case "alignTopCenter":
      return (
        <SkiliticsIcon
          icon={flexDirection?.includes("column") ? "flow-horizontal-middle-left" : "flow-horizontal-top-center"}
          flip={flexDirection === "column-reverse" ? "vertical" : undefined}
          rotation={flexDirection?.includes("column") ? 90 : undefined}
        />
      );
    case "alignTopRight":
      return (
        <SkiliticsIcon
          icon={"flow-horizontal-top-left"}
          flip={flexDirection === "column" ? "both" : flexDirection?.includes("reverse") ? "horizontal" : undefined}
          rotation={flexDirection?.includes("column") ? 270 : undefined}
        />
      );
    case "alignLeft":
      return (
        <SkiliticsIcon
          icon={flexDirection?.includes("column") ? "flow-horizontal-top-center" : "flow-horizontal-middle-left"}
          flip={flexDirection === "row-reverse" ? "horizontal" : undefined}
          rotation={flexDirection?.includes("column") ? 270 : undefined}
        />
      );
    case "alignCenter":
      return (
        <SkiliticsIcon
          icon={"flow-horizontal-middle-center"}
          rotation={flexDirection?.includes("column") ? 90 : undefined}
        />
      );
    case "alignRight":
      return (
        <SkiliticsIcon
          icon={flexDirection?.includes("column") ? "flow-horizontal-top-center" : "flow-horizontal-middle-left"}
          flip={
            flexDirection === "column" ? "horizontal" : flexDirection?.includes("reverse") ? "horizontal" : undefined
          }
          rotation={flexDirection?.includes("column") ? 270 : undefined}
        />
      );
    case "alignBottomLeft":
      return (
        <SkiliticsIcon
          icon={"flow-horizontal-top-left"}
          flip={flexDirection === "column" ? "horizontal" : flexDirection?.includes("reverse") ? "both" : "vertical"}
          rotation={flexDirection?.includes("column") ? 90 : undefined}
        />
      );
    case "alignBottomCenter":
      return (
        <SkiliticsIcon
          icon={flexDirection?.includes("column") ? "flow-horizontal-middle-left" : "flow-horizontal-top-center"}
          flip={flexDirection === "column" ? "horizontal" : flexDirection?.includes("reverse") ? "both" : "vertical"}
          rotation={flexDirection?.includes("column") ? 90 : undefined}
        />
      );
    case "alignBottomRight":
      return (
        <SkiliticsIcon
          icon={"flow-horizontal-top-left"}
          flip={flexDirection === "column" ? undefined : flexDirection === "row-reverse" ? "both" : "vertical"}
          rotation={flexDirection?.includes("column") ? 90 : undefined}
        />
      );
    default:
      return null;
  }
};
